// Single shared API client. Every src/api/*.js imports authFetch from here
// (previously each file re-declared an identical copy). It preserves the HTTP
// status on failures (ApiError.status) and routes 401s through a global
// auth-error handler so an expired session forces a clean re-login instead of
// silently-failing background polls.
import { getToken } from './auth';
import { recordHttp } from '../utils/interactionLog';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// `?owner=` / `&owner=` for a shared read, or '' for the caller's own project.
// Lives here rather than being re-declared per api module: nearly every read
// route delegates now (see the backend's tests/test_shared_read_coverage.py), so
// this is the single spelling of the param that makes a shared project show its
// geometry, mesh and results instead of an empty shell.
export const ownerParam = (owner, sep = '?') =>
  owner ? `${sep}owner=${encodeURIComponent(owner)}` : '';

export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

// App.js registers a handler (sign out + redirect + toast). Kept out of the api
// layer because these modules can't navigate. Debounced so a burst of
// concurrent 401s (many polling panels) triggers exactly one re-login.
let _authErrorHandler = null;
let _handling = false;

export function setAuthErrorHandler(fn) {
  _authErrorHandler = fn;
}

function triggerAuthError() {
  if (_handling) return;
  _handling = true;
  try {
    _authErrorHandler?.();
  } finally {
    setTimeout(() => {
      _handling = false;
    }, 3000);
  }
}

export async function authFetch(path, options = {}) {
  const token = await getToken();
  if (!token) {
    triggerAuthError();
    throw new ApiError('Your session has expired. Please sign in again.', 401);
  }
  let res;
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });
  } catch {
    throw new ApiError('Could not reach the server. Check your connection and try again.', 0);
  }
  // Every mutation in the app passes through here, which is what lets Quality
  // Check record a replayable session without per-tile code. Recorded AFTER the
  // response so a request that failed is not replayed as though it worked; reads,
  // ignored paths and agent/tutorial-driven calls are filtered inside recordHttp.
  if (res.ok) recordHttp({ method: options.method || 'GET', path, body: options.body });
  if (res.status === 401) {
    triggerAuthError();
    throw new ApiError('Your session has expired. Please sign in again.', 401);
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new ApiError(err.detail || `Request failed: ${res.status}`, res.status);
  }
  return res.json();
}

// For blob / multipart / non-JSON endpoints: same auth + 401 handling, but
// returns the raw Response (caller reads .blob()/.json()). No forced
// Content-Type so FormData sets its own multipart boundary.
export async function authFetchRaw(path, options = {}) {
  const token = await getToken();
  if (!token) {
    triggerAuthError();
    throw new ApiError('Your session has expired. Please sign in again.', 401);
  }
  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: { Authorization: `Bearer ${token}`, ...options.headers },
  });
  if (res.status === 401) {
    triggerAuthError();
    throw new ApiError('Your session has expired. Please sign in again.', 401);
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ApiError(text || `Request failed: ${res.status}`, res.status);
  }
  // Multipart/blob bodies (FormData) are not JSON, so only the method and path are
  // recorded — enough for the timeline to show that an upload happened, and the
  // uploaded FILE itself survives in the user's repository, which is where a replay
  // reads it from anyway (repository entries are user-global, not per-project).
  recordHttp({ method: options.method || 'GET', path, body: undefined });
  return res;
}
